import React from 'react';
import { Link } from 'react-router-dom';
import logo1x from 'images/instaacard-logo-1x.png';
import logo2x from 'images/instaacard-logo-2x.png';
import logo3x from 'images/instaacard-logo-3x.png';
import logoWhite1x from 'images/instaacard-logo-white-1x.png';
import logoWhite2x from 'images/instaacard-logo-white-2x.png';
import logoWhite3x from 'images/instaacard-logo-white-3x.png';

export interface LogoProps {
    img?: string;
    imgLight?: string;
    className?: string;
    style?: object;
}

/** One file per pixel density, chosen by the browser. */
const srcSet = (x1: string, x2: string, x3: string) => `${x1} 1x, ${x2} 2x, ${x3} 3x`;

const COLOUR_SET = srcSet(logo1x, logo2x, logo3x);
const WHITE_SET = srcSet(logoWhite1x, logoWhite2x, logoWhite3x);

/**
 * The InstaaCard wordmark with the registered mark (®).
 *
 *   img       blue + gold, for light backgrounds
 *   imgLight  white + gold, for dark mode
 *
 * Both are cropped from the same brand artwork ("Instaacard Logo with R" and
 * "Instaacard Logo In White"), so they come out at identical dimensions and the
 * header does not shift when the theme changes. The tagline is deliberately left
 * off: at the 32px mobile header it would be unreadable.
 *
 * SHARPNESS. Each colourway ships at exactly 175, 350 and 525px wide -- the
 * header's 175 CSS px at 1x, 2x and 3x density -- and `srcSet` lets every
 * screen load the one that matches it pixel for pixel. Before, a single 1420px
 * image was shrunk by the browser on the fly, which is a bilinear resample:
 * it samples only a few source pixels per output pixel, so at a 4-8x reduction
 * thin strokes went soft. These were downscaled ahead of time by area averaging
 * in premultiplied alpha, which reads every source pixel and keeps the edges
 * clean without a dark halo. They are also far lighter (6.5KB at 2x, against
 * 28KB for the one large file).
 *
 * The brand SVG cannot be used instead: it is a CorelDRAW export whose text was
 * never converted to outlines and relies on SVG fonts, which no current browser
 * renders. A re-export with text converted to curves would beat all of this.
 *
 * Dark mode used to show `logo-light.svg`, which was the Ciseco theme's logo
 * left over from the template this site was built on -- so anyone browsing in
 * dark mode saw "Ciseco." in the header and footer.
 *
 * `img` / `imgLight` are honoured when passed (they used to be ignored). A
 * caller-supplied image is used as-is, without the density set, since only the
 * bundled logo has sized variants.
 */
const Logo: React.FC<LogoProps> = ({ img, imgLight, className = 'flex-shrink-0', style = {} }) => {
    const lightSrc = img ?? logo1x;
    const lightSet = img ? undefined : COLOUR_SET;
    const darkSrc = imgLight ?? logoWhite1x;
    const darkSet = imgLight ? undefined : WHITE_SET;

    return (
        <Link to="/" className={`ttnc-logo inline-block text-slate-600 ${className}`} style={style}>
            <img
                className="block h-auto max-h-8 sm:max-h-10 dark:hidden"
                src={lightSrc}
                srcSet={lightSet}
                alt="InstaaCard"
                width="175"
                height="28"
            />
            <img
                className="hidden h-auto max-h-8 sm:max-h-10 dark:block"
                src={darkSrc}
                srcSet={darkSet}
                alt="InstaaCard"
                width="175"
                height="28"
            />
        </Link>
    );
};

export default Logo;
